import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabase';
import Home from './pages/Home';
import JobDetail from './pages/JobDetail';
import PostJob from './pages/PostJob';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Pagamenti from './pages/Pagamenti';
import StripeAccountManager from './pages/StripeAccountManager';
import PoliticaCancellazione from './pages/PoliticaCancellazione';
import OnboardingStatus from './pages/OnboardingStatus';
import Chat from './pages/Chat';
import ChatDetail from './pages/ChatDetail';
import Guide from './pages/Guide';
import EditJob from './pages/EditJob';
import ReviewPage from './pages/ReviewPage';
import PublicProfile from './pages/PublicProfile';
import Admin from './pages/Admin';
import Calendario from './pages/Calendario';
import UserSearch from './pages/UserSearch';
import ReportProblem from './pages/ReportProblem';
import Preferiti from './pages/Preferiti';
import PushPermissionBanner from './PushPermissionBanner';
import { initServiceWorkerSilently } from './pushNotifications';
import { NotificationCountsProvider } from './NotificationContext';
import { usePageTracking } from './hooks/useAnalytics';
 
// Componente interno: usePageTracking usa useLocation, quindi deve stare
// DENTRO il BrowserRouter. Non fa altro che tracciare i cambi pagina.
function AnalyticsTracker() {
  usePageTracking();
  return null;
}
 
function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
 
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      setLoading(false);
    });
 
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
    });
 
    return () => subscription.unsubscribe();
  }, []);
 
  useEffect(() => {
    initServiceWorkerSilently();
  }, []);
 
  const fetchProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, phone, bio, city, birth_date, identity_status')
      .eq('id', userId)
      .single();
    if (data) setProfile(data);
  };
 
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#2d7a3a', fontSize: 20 }}>
      Caricamento Jobbetty...
    </div>
  );
 
  return (
    <NotificationCountsProvider session={session}>
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          <Route path="/login" element={!session ? <Login /> : <Navigate to="/" />} />
          <Route path="/register" element={!session ? <Register /> : <Navigate to="/" />} />
          <Route path="/" element={session ? <Home session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/job/:id" element={session ? <JobDetail session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/pubblica" element={session ? <PostJob session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/profilo" element={session ? <Profile session={session} profile={profile} onProfileUpdate={fetchProfile} /> : <Navigate to="/login" />} />
          <Route path="/pagamenti" element={session ? <Pagamenti session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/account-stripe" element={session ? <StripeAccountManager session={session} /> : <Navigate to="/login" />} />
          <Route path="/politica-cancellazione" element={session ? <PoliticaCancellazione /> : <Navigate to="/login" />} />
          <Route path="/onboarding-status" element={session ? <OnboardingStatus /> : <Navigate to="/login" />} />
          <Route path="/chat" element={session ? <Chat session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/chat/:conversationId" element={session ? <ChatDetail session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/guide" element={session ? <Guide /> : <Navigate to="/login" />} />
          <Route path="/modifica-annuncio/:id" element={session ? <EditJob session={session} /> : <Navigate to="/login" />} />
          <Route path="/recensione/:applicationId" element={session ? <ReviewPage session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/utente/:userId" element={session ? <PublicProfile session={session} /> : <Navigate to="/login" />} />
          <Route path="/admin" element={session ? <Admin session={session} /> : <Navigate to="/login" />} />
          <Route path="/calendario" element={session ? <Calendario session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/cerca-utente" element={session ? <UserSearch /> : <Navigate to="/login" />} />
          <Route path="/segnala-problema" element={session ? <ReportProblem session={session} profile={profile} /> : <Navigate to="/login" />} />
          <Route path="/preferiti" element={session ? <Preferiti session={session} /> : <Navigate to="/login" />} />
        </Routes>
        {session && <PushPermissionBanner session={session} />}
      </BrowserRouter>
    </NotificationCountsProvider>
  );
}
 
export default App;